import React from 'react';

/**
 * Creates Order table column definitions
 */
export const getOrderColumns = () => [
  {
    key: 'id',
    label: 'Order ID',
    render: (val, row) => (
      <span style={{
        fontFamily: 'ui-monospace, monospace',
        fontWeight: 700,
        color: '#475569',
        background: '#f1f5f9',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '0.78rem',
        border: '1px solid #e2e8f0',
        whiteSpace: 'nowrap'
      }}>
        {val || row.id || 'ORD'}
      </span>
    )
  },
  {
    key: 'customer',
    label: 'Customer Name',
    render: (val) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
        <div style={{
          width: '28px',
          height: '28px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
          color: '#3730a3',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.75rem',
          fontWeight: 700,
          flexShrink: 0
        }}>
          {(val || 'C').charAt(0).toUpperCase()}
        </div>
        <span style={{ fontWeight: 600, color: '#0f172a' }}>{val || 'Customer'}</span>
      </div>
    )
  },
  {
    key: 'email',
    label: 'Email',
    render: (val) => (
      <a
        href={`mailto:${val}`}
        style={{ color: '#0284c7', textDecoration: 'none', fontSize: '0.84rem', fontWeight: 500, whiteSpace: 'nowrap' }}
        onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
        onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
      >
        {val || '—'}
      </a>
    )
  },
  {
    key: 'items',
    label: 'Items',
    render: (val) => (
      <span style={{
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        padding: '3px 8px',
        borderRadius: '6px',
        fontSize: '0.82rem',
        fontWeight: 600,
        color: '#334155',
        whiteSpace: 'nowrap'
      }}>
        🛍️ {val || '1 item'}
      </span>
    )
  },
  {
    key: 'total',
    label: 'Total Amount',
    render: (val, row) => (
      <span style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
        ₹{Number(val || row.total || 0).toLocaleString('en-IN')}
      </span>
    )
  },
  {
    key: 'payment',
    label: 'Payment Method',
    render: (val) => (
      <span style={{
        background: '#f1f5f9',
        color: '#334155',
        fontWeight: 600,
        padding: '4px 10px',
        borderRadius: '8px',
        fontSize: '0.8rem',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        whiteSpace: 'nowrap'
      }}>
        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#16a34a' }}></span>
        {val || 'Online'}
      </span>
    )
  },
  {
    key: 'date',
    label: 'Date',
    render: (val) => (
      <span style={{ color: '#64748b', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
        {val || '—'}
      </span>
    )
  },
  {
    key: 'status',
    label: 'Status',
    render: (val) => {
      const isPaid = !val || ['paid', 'completed', 'success', 'confirmed'].includes(String(val).toLowerCase());
      return (
        <span className={`status-pill ${isPaid ? 'paid' : 'pending'}`}>
          {isPaid ? 'PAID' : val}
        </span>
      );
    }
  }
];

/**
 * Creates Registration table column definitions
 */
export const getRegistrationColumns = () => [
  {
    key: 'id',
    label: 'Reg ID',
    render: (val, row) => (
      <span style={{
        fontFamily: 'ui-monospace, monospace',
        fontWeight: 700,
        color: '#475569',
        background: '#f1f5f9',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '0.78rem',
        border: '1px solid #e2e8f0',
        whiteSpace: 'nowrap'
      }}>
        {val || row.id || 'REG'}
      </span>
    )
  },
  {
    key: 'name',
    label: 'Full Name',
    render: (val) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
        <div style={{
          width: '28px',
          height: '28px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
          color: '#166534',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.75rem',
          fontWeight: 700,
          flexShrink: 0
        }}>
          {(val || 'R').charAt(0).toUpperCase()}
        </div>
        <span style={{ fontWeight: 600, color: '#0f172a' }}>{val}</span>
      </div>
    )
  },
  {
    key: 'phone',
    label: 'Phone',
    render: (val) => (
      <a href={`tel:${val}`} style={{ color: '#475569', textDecoration: 'none', fontSize: '0.84rem', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
        {val || '—'}
      </a>
    )
  },
  {
    key: 'email',
    label: 'Email',
    render: (val) => (
      <a
        href={`mailto:${val}`}
        style={{ color: '#0284c7', textDecoration: 'none', fontSize: '0.84rem', fontWeight: 500, whiteSpace: 'nowrap' }}
        onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
        onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
      >
        {val || '—'}
      </a>
    )
  },
  {
    key: 'workshop',
    label: 'Workshop Event',
    render: (val) => (
      <span style={{ fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
        🧘 {val || 'Workshop'}
      </span>
    )
  },
  {
    key: 'batch',
    label: 'Batch Timing',
    render: (val) => val ? (
      <span style={{ background: '#f8fafc', color: '#334155', border: '1px solid #e2e8f0', padding: '3px 8px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
        ⏰ {val}
      </span>
    ) : <span style={{ color: '#9ca3af' }}>-</span>
  },
  {
    key: 'couponCode',
    label: 'Coupon Code',
    render: (val) => val ? (
      <span style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', padding: '3px 8px', borderRadius: '6px', fontWeight: 700, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
        🏷️ {val}
      </span>
    ) : <span style={{ color: '#9ca3af' }}>—</span>
  },
  {
    key: 'date',
    label: 'Date',
    render: (val) => (
      <span style={{ color: '#64748b', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
        {val || '—'}
      </span>
    )
  },
  {
    key: 'status',
    label: 'Status',
    render: (val) => {
      const isPaid = !val || ['paid', 'completed', 'success', 'confirmed', 'registered'].includes(String(val).toLowerCase());
      return (
        <span className={`status-pill ${isPaid ? 'paid' : 'pending'}`}>
          {isPaid ? 'PAID' : val}
        </span>
      );
    }
  }
];

/**
 * Creates User table column definitions
 */
export const getUserColumns = ({ currentUser, updatingUserId, onToggleUserStatus }) => [
  {
    key: 'id',
    label: 'User ID',
    render: (val, row) => (
      <span style={{
        fontFamily: 'ui-monospace, monospace',
        fontWeight: 700,
        color: '#475569',
        background: '#f1f5f9',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '0.78rem',
        border: '1px solid #e2e8f0',
        whiteSpace: 'nowrap'
      }}>
        {val || row.id || 'USR'}
      </span>
    )
  },
  {
    key: 'name',
    label: 'Name',
    render: (val) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
        <div style={{
          width: '28px',
          height: '28px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
          color: '#6b21a8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.75rem',
          fontWeight: 700,
          flexShrink: 0
        }}>
          {(val || 'U').charAt(0).toUpperCase()}
        </div>
        <span style={{ fontWeight: 600, color: '#0f172a' }}>{val}</span>
      </div>
    )
  },
  {
    key: 'email',
    label: 'Email',
    render: (val) => (
      <a
        href={`mailto:${val}`}
        style={{ color: '#0284c7', textDecoration: 'none', fontSize: '0.84rem', fontWeight: 500, whiteSpace: 'nowrap' }}
        onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
        onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
      >
        {val || '—'}
      </a>
    )
  },
  {
    key: 'role',
    label: 'Role',
    render: (val) => (
      <span style={{
        background: val === 'admin' ? '#ede9fe' : '#f1f5f9',
        color: val === 'admin' ? '#6b21a8' : '#334155',
        border: val === 'admin' ? '1px solid #ddd6fe' : '1px solid #e2e8f0',
        padding: '3px 9px',
        borderRadius: '10px',
        fontSize: '0.76rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        whiteSpace: 'nowrap'
      }}>
        {val || 'User'}
      </span>
    )
  },
  {
    key: 'dateJoined',
    label: 'Date Joined',
    render: (val) => (
      <span style={{ color: '#64748b', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
        {val || '—'}
      </span>
    )
  },
  {
    key: 'status',
    label: 'Account Status',
    render: (val, row) => {
      const isUserActive = row.isActive !== false && String(val || row.status || '').toLowerCase() !== 'inactive';
      return (
        <span className={`status-pill ${isUserActive ? 'active' : 'inactive'}`}>
          {isUserActive ? 'Active' : 'Inactive'}
        </span>
      );
    }
  },
  {
    key: 'actions',
    label: 'Login Access & Actions',
    render: (_, row) => {
      const targetUserId = row._id || row.id;
      const isUserActive = row.isActive !== false && String(row.status || '').toLowerCase() !== 'inactive';
      const isCurrentAdmin = currentUser && (currentUser.email === row.email || currentUser._id === targetUserId);
      const isBusy = updatingUserId === targetUserId;

      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
          <button
            type="button"
            className={`user-toggle-btn ${isUserActive ? 'deactivate' : 'activate'}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleUserStatus(row);
            }}
            disabled={isBusy}
            title={
              isUserActive
                ? "Deactivate user (blocks login across website, student portal, & dashboard)"
                : "Activate user (restore login access)"
            }
          >
            {isBusy ? (
              <span>Saving...</span>
            ) : isUserActive ? (
              <>
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                </svg>
                <span>Deactivate</span>
              </>
            ) : (
              <>
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>Activate</span>
              </>
            )}
          </button>
          {isCurrentAdmin && (
            <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600 }}>(You)</span>
          )}
        </div>
      );
    }
  }
];

/**
 * Creates Workshop table column definitions
 */
export const getWorkshopColumns = ({ onEditWorkshop, onToggleStatus } = {}) => [
  {
    key: 'id',
    label: 'ID',
    render: (val, row, index) => {
      const displayVal = typeof val === 'number' ? val : (index !== undefined ? index + 1 : val);
      return (
        <span style={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          fontWeight: 700,
          color: '#64748b',
          background: '#f8fafc',
          padding: '4px 8px',
          borderRadius: '6px',
          fontSize: '0.76rem',
          border: '1px solid #e2e8f0',
          letterSpacing: '0.5px'
        }}>
          #{String(displayVal).padStart(2, '0')}
        </span>
      );
    }
  },
  {
    key: 'type',
    label: 'Type',
    render: (val, row) => {
      const isFree = String(val || '').toUpperCase() === 'FREE' || Number(row.price || 0) === 0;
      return (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 10px',
          borderRadius: '8px',
          fontSize: '0.72rem',
          fontWeight: 700,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          background: isFree ? '#ecfdf5' : '#eff6ff',
          color: isFree ? '#065f46' : '#1e40af',
          border: isFree ? '1px solid #a7f3d0' : '1px solid #bfdbfe',
          whiteSpace: 'nowrap'
        }}>
          <span style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: isFree ? '#10b981' : '#3b82f6',
            boxShadow: isFree ? '0 0 6px #10b981' : '0 0 6px #3b82f6'
          }} />
          {isFree ? 'FREE' : 'PAID'}
        </span>
      );
    }
  },
  {
    key: 'title',
    label: 'Workshop Title',
    render: (val, row) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: '220px' }}>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '10px',
          background: row.type === 'FREE'
            ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)'
            : 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          border: row.type === 'FREE' ? '1px solid #a7f3d0' : '1px solid #bfdbfe'
        }}>
          {row.type === 'FREE' ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"></path>
              <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"></path>
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="5" r="3"></circle>
              <path d="M6 10l6 4 6-4"></path>
              <path d="M4 14l8 6 8-6"></path>
            </svg>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.88rem', letterSpacing: '-0.2px' }}>
            {val}
          </span>
          {row.subtitle && (
            <span style={{ fontSize: '0.74rem', color: '#64748b', fontWeight: 500 }}>
              {row.subtitle}
            </span>
          )}
        </div>
      </div>
    )
  },
  {
    key: 'category',
    label: 'Category',
    render: (val) => (
      <span style={{
        background: '#f8fafc',
        color: '#334155',
        border: '1px solid #e2e8f0',
        padding: '4px 10px',
        borderRadius: '8px',
        fontSize: '0.76rem',
        fontWeight: 600,
        whiteSpace: 'nowrap'
      }}>
        {val || 'General'}
      </span>
    )
  },
  {
    key: 'duration',
    label: 'Duration',
    render: (val) => (
      <span style={{
        background: '#ffffff',
        color: '#475569',
        border: '1px solid #e2e8f0',
        padding: '4px 10px',
        borderRadius: '8px',
        fontSize: '0.78rem',
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        whiteSpace: 'nowrap'
      }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
        {val || '—'}
      </span>
    )
  },
  {
    key: 'price',
    label: 'Price',
    render: (val, row) => {
      const isFree = Number(val || 0) === 0 || String(row.type || '').toUpperCase() === 'FREE';
      if (isFree) {
        return (
          <span style={{
            color: '#059669',
            fontWeight: 700,
            fontSize: '0.82rem',
            background: '#ecfdf5',
            padding: '3px 8px',
            borderRadius: '6px',
            border: '1px solid #a7f3d0'
          }}>
            Free
          </span>
        );
      }
      const sym = row.currency === 'INR' ? '₹' : '$';
      return (
        <span style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.92rem', letterSpacing: '-0.2px' }}>
          {sym}{Number(val).toLocaleString()}
        </span>
      );
    }
  },
  {
    key: 'registrationsCount',
    label: 'Enrolled',
    render: (val, row) => (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        background: '#f8fafc',
        padding: '4px 10px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        fontSize: '0.82rem',
        fontWeight: 600,
        color: '#1e293b',
        whiteSpace: 'nowrap'
      }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
        <strong>{val || 0}</strong> <span style={{ color: '#64748b', fontSize: '0.74rem' }}>{row.type === 'PAID' ? 'orders' : 'attendees'}</span>
      </span>
    )
  },
  {
    key: 'revenue',
    label: 'Revenue',
    render: (val, row) => {
      const num = Number(val || 0);
      if (num === 0) {
        return <span style={{ color: '#cbd5e1', fontWeight: 600 }}>—</span>;
      }
      const sym = row.currency === 'INR' ? '₹' : '$';
      return (
        <span style={{
          fontWeight: 800,
          color: '#047857',
          fontSize: '0.9rem',
          fontVariantNumeric: 'tabular-nums',
          background: '#f0fdf4',
          padding: '3px 8px',
          borderRadius: '6px',
          border: '1px solid #bbf7d0',
          whiteSpace: 'nowrap'
        }}>
          {sym}{num.toLocaleString()}
        </span>
      );
    }
  },
  {
    key: 'status',
    label: 'Status',
    render: (val, row) => {
      const isInactive = String(val || '').toLowerCase() === 'inactive';
      return (
        <span
          style={{
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 10px',
            borderRadius: '8px',
            fontSize: '0.72rem',
            fontWeight: 700,
            letterSpacing: '0.4px',
            background: isInactive ? '#fff1f2' : '#f0fdf4',
            color: isInactive ? '#be123c' : '#15803d',
            border: isInactive ? '1px solid #fecdd3' : '1px solid #bbf7d0',
            transition: 'all 0.15s ease',
            whiteSpace: 'nowrap'
          }}
          onClick={() => onToggleStatus && onToggleStatus(row)}
          title="Click to toggle Active / Inactive"
        >
          <span style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: isInactive ? '#e11d48' : '#22c55e',
            boxShadow: isInactive ? '0 0 6px #e11d48' : '0 0 6px #22c55e'
          }} />
          {isInactive ? 'INACTIVE' : 'ACTIVE'}
        </span>
      );
    }
  },
  {
    key: 'actions',
    label: 'Actions',
    render: (_, row) => {
      const isInactive = String(row.status || '').toLowerCase() === 'inactive';
      return (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
          <button
            style={{
              padding: '6px 12px',
              borderRadius: '7px',
              border: '1px solid #e2e8f0',
              cursor: 'pointer',
              background: '#ffffff',
              color: '#334155',
              fontWeight: 600,
              fontSize: '0.78rem',
              transition: 'all 0.15s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
            }}
            onClick={() => onEditWorkshop && onEditWorkshop(row)}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#5c8862';
              e.currentTarget.style.color = '#5c8862';
              e.currentTarget.style.background = '#f0fdf4';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e2e8f0';
              e.currentTarget.style.color = '#334155';
              e.currentTarget.style.background = '#ffffff';
            }}
            title="Edit workshop details"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
            Edit
          </button>
          <button
            style={{
              padding: '6px 12px',
              borderRadius: '7px',
              border: isInactive ? '1px solid #bbf7d0' : '1px solid #fecdd3',
              cursor: 'pointer',
              background: isInactive ? '#f0fdf4' : '#fff1f2',
              color: isInactive ? '#166534' : '#991b1b',
              fontWeight: 600,
              fontSize: '0.78rem',
              transition: 'all 0.15s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onClick={() => onToggleStatus && onToggleStatus(row)}
            title={isInactive ? 'Publish workshop on website' : 'Hide workshop from website'}
          >
            <span style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: isInactive ? '#22c55e' : '#f43f5e'
            }} />
            {isInactive ? 'Activate' : 'Deactivate'}
          </button>
        </div>
      );
    }
  }
];

/**
 * Creates Coupon table column definitions
 */
export const getCouponColumns = ({ copiedCode, onCopyCode, onEditCoupon, onToggleCoupon, onDeleteCoupon }) => [
  {
    key: 'code',
    label: 'Coupon Code',
    render: (val) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
        <strong style={{
          background: '#f8fafc',
          border: '1.5px dashed #5c8862',
          color: '#166534',
          padding: '4px 10px',
          borderRadius: '8px',
          fontSize: '0.88rem',
          fontFamily: 'monospace',
          letterSpacing: '0.5px'
        }}>
          🏷️ {val}
        </strong>
        <button
          onClick={() => onCopyCode(val)}
          style={{
            padding: '4px 10px',
            fontSize: '0.75rem',
            borderRadius: '6px',
            border: copiedCode === val ? '1px solid #86efac' : '1px solid #e2e8f0',
            background: copiedCode === val ? '#dcfce7' : '#ffffff',
            color: copiedCode === val ? '#15803d' : '#475569',
            cursor: 'pointer',
            fontWeight: 600,
            transition: 'all 0.15s ease'
          }}
          title="Copy Code to Clipboard"
        >
          {copiedCode === val ? '✓ Copied' : '📋 Copy'}
        </button>
      </div>
    )
  },
  {
    key: 'discountValue',
    label: 'Discount',
    render: (val, row) => (
      <span style={{
        background: '#ecfdf5',
        color: '#047857',
        border: '1px solid #a7f3d0',
        padding: '4px 10px',
        borderRadius: '8px',
        fontWeight: 700,
        fontSize: '0.82rem',
        whiteSpace: 'nowrap'
      }}>
        {row.discountType === 'percentage' ? `${val}% OFF` : `₹${val} OFF`}
      </span>
    )
  },
  {
    key: 'minAmount',
    label: 'Min Purchase',
    render: (val) => <span style={{ fontWeight: 600, color: '#334155', whiteSpace: 'nowrap' }}>₹{val}</span>
  },
  {
    key: 'applicableWorkshop',
    label: 'Workshop',
    render: (val) => <span style={{ color: '#475569', fontSize: '0.86rem', whiteSpace: 'nowrap' }}>{val || 'All Workshops'}</span>
  },
  {
    key: 'usedCount',
    label: 'Uses',
    render: (val, row) => (
      <span style={{ background: '#f1f5f9', color: '#475569', padding: '3px 8px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
        {val} / {row.usageLimit}
      </span>
    )
  },
  {
    key: 'isActive',
    label: 'Status',
    render: (val) => (
      <span className={`status-pill ${val ? 'active' : 'pending'}`}>
        {val ? 'ACTIVE' : 'INACTIVE'}
      </span>
    )
  },
  {
    key: '_id',
    label: 'Actions',
    render: (id, row) => (
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', whiteSpace: 'nowrap' }}>
        <button
          style={{
            padding: '5px 12px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            cursor: 'pointer',
            background: '#ffffff',
            color: '#334155',
            fontWeight: 600,
            fontSize: '0.78rem',
            transition: 'all 0.15s ease'
          }}
          onClick={() => onEditCoupon(row)}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#5c8862'; e.currentTarget.style.color = '#5c8862'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#334155'; }}
        >
          ✏️ Edit
        </button>
        <button
          style={{
            padding: '5px 12px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            cursor: 'pointer',
            background: '#ffffff',
            color: '#475569',
            fontSize: '0.78rem',
            fontWeight: 600,
            transition: 'all 0.15s ease'
          }}
          onClick={() => onToggleCoupon(row._id || id)}
        >
          {row.isActive ? 'Disable' : 'Enable'}
        </button>
        <button
          style={{
            padding: '5px 12px',
            borderRadius: '8px',
            border: '1px solid #fecdd3',
            background: '#fff1f2',
            color: '#be123c',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.78rem',
            transition: 'all 0.15s ease'
          }}
          onClick={() => onDeleteCoupon(row._id || id)}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#ffe4e6'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#fff1f2'; }}
        >
          🗑️ Delete
        </button>
      </div>
    )
  }
];

/**
 * Creates Inquiry table column definitions
 */
export const getInquiryColumns = ({ onSelectInquiry, onToggleInquiryStatus, onDeleteInquiry }) => [
  {
    key: 'id',
    label: 'MSG ID',
    render: (val, row) => (
      <span style={{
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        fontSize: '0.78rem',
        fontWeight: 600,
        color: '#475569',
        background: '#f1f5f9',
        padding: '4px 8px',
        borderRadius: '6px',
        letterSpacing: '0.3px',
        border: '1px solid #e2e8f0',
        whiteSpace: 'nowrap'
      }}>
        {val || row.id || 'MSG'}
      </span>
    )
  },
  {
    key: 'name',
    label: 'Contact Name',
    render: (val) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{
          width: '28px',
          height: '28px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
          color: '#3730a3',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.75rem',
          fontWeight: 700,
          flexShrink: 0
        }}>
          {(val || 'U').charAt(0).toUpperCase()}
        </div>
        <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.88rem', whiteSpace: 'nowrap' }}>{val}</span>
      </div>
    )
  },
  {
    key: 'email',
    label: 'Email ID',
    render: (val) => (
      <a
        href={`mailto:${val}`}
        style={{
          color: '#0284c7',
          textDecoration: 'none',
          fontSize: '0.84rem',
          fontWeight: 500,
          whiteSpace: 'nowrap'
        }}
        onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
        onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
        title={`Send email to ${val}`}
      >
        {val}
      </a>
    )
  },
  {
    key: 'phone',
    label: 'Phone',
    render: (val) => (
      <a
        href={`tel:${val}`}
        style={{
          color: '#475569',
          textDecoration: 'none',
          fontSize: '0.84rem',
          fontWeight: 500,
          whiteSpace: 'nowrap'
        }}
      >
        {val}
      </a>
    )
  },
  {
    key: 'category',
    label: 'Category',
    render: (val) => (
      <span style={{
        background: '#ede9fe',
        color: '#6b21a8',
        padding: '3px 10px',
        borderRadius: '12px',
        fontWeight: 600,
        fontSize: '0.78rem',
        display: 'inline-block',
        border: '1px solid #ddd6fe',
        whiteSpace: 'nowrap'
      }}>
        {val || 'General'}
      </span>
    )
  },
  {
    key: 'message',
    label: 'Message',
    onCellClick: (row) => onSelectInquiry(row),
    render: (val, row) => {
      const text = val || row?.message || 'No message provided';
      return (
        <div
          onClick={(e) => {
            e.stopPropagation();
            onSelectInquiry(row);
          }}
          title="Click to view complete message popup"
          style={{
            maxWidth: '280px',
            minWidth: '220px',
            padding: '8px 12px',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'all 0.18s ease',
            color: '#1e293b',
            boxSizing: 'border-box'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#5c8862';
            e.currentTarget.style.background = '#f0fdf4';
            e.currentTarget.style.boxShadow = '0 3px 10px rgba(92, 136, 98, 0.15)';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e2e8f0';
            e.currentTarget.style.background = '#f8fafc';
            e.currentTarget.style.boxShadow = 'none';
            e.currentTarget.style.transform = 'none';
          }}
        >
          <div style={{
            fontSize: '0.82rem',
            color: '#334155',
            lineHeight: '1.45',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            wordBreak: 'break-word',
            marginBottom: '5px'
          }}>
            {text}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.72rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '3px' }}>
              <span>💬</span> {text.length > 50 ? `${text.length} chars` : 'Note'}
            </span>
            <span style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              color: '#5c8862',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '2px'
            }}>
              Full View ↗
            </span>
          </div>
        </div>
      );
    }
  },
  {
    key: 'city',
    label: 'City',
    render: (val) => (
      <span style={{ color: '#475569', fontSize: '0.84rem', whiteSpace: 'nowrap' }}>
        {val || '—'}
      </span>
    )
  },
  {
    key: 'date',
    label: 'Date',
    render: (val) => (
      <span style={{ color: '#64748b', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
        {val || '—'}
      </span>
    )
  },
  {
    key: 'status',
    label: 'Status',
    render: (val) => (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '0.74rem',
        fontWeight: 700,
        letterSpacing: '0.3px',
        textTransform: 'uppercase',
        background: val === 'Resolved' ? '#dcfce7' : '#fffbeb',
        color: val === 'Resolved' ? '#15803d' : '#b45309',
        border: val === 'Resolved' ? '1px solid #86efac' : '1px solid #fde68a',
        whiteSpace: 'nowrap'
      }}>
        <span style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          background: val === 'Resolved' ? '#16a34a' : '#d97706'
        }} />
        {val || 'Pending'}
      </span>
    )
  },
  {
    key: '_id',
    label: 'Actions',
    render: (id, row) => (
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', whiteSpace: 'nowrap' }}>
        <button
          style={{
            padding: '6px 12px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            cursor: 'pointer',
            background: '#ffffff',
            color: '#334155',
            fontWeight: 600,
            fontSize: '0.78rem',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            transition: 'all 0.15s ease',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
          }}
          onClick={(e) => {
            e.stopPropagation();
            onSelectInquiry(row);
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#5c8862';
            e.currentTarget.style.color = '#5c8862';
            e.currentTarget.style.background = '#f0fdf4';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e2e8f0';
            e.currentTarget.style.color = '#334155';
            e.currentTarget.style.background = '#ffffff';
          }}
          title="Open complete message popup"
        >
          <span>💬</span> View
        </button>
        <button
          style={{
            padding: '6px 12px',
            borderRadius: '8px',
            border: row.status === 'Resolved' ? '1px solid #fde68a' : '1px solid #86efac',
            cursor: 'pointer',
            background: row.status === 'Resolved' ? '#fffbeb' : '#ecfdf5',
            color: row.status === 'Resolved' ? '#b45309' : '#047857',
            fontWeight: 600,
            fontSize: '0.78rem',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            transition: 'all 0.15s ease'
          }}
          onClick={(e) => {
            e.stopPropagation();
            onToggleInquiryStatus(row._id || id);
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
          title={row.status === 'Resolved' ? 'Reopen inquiry' : 'Mark as resolved'}
        >
          {row.status === 'Resolved' ? '↺ Reopen' : '✓ Resolve'}
        </button>
        <button
          style={{
            padding: '6px 12px',
            borderRadius: '8px',
            border: '1px solid #fecdd3',
            background: '#fff1f2',
            color: '#be123c',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.78rem',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            transition: 'all 0.15s ease'
          }}
          onClick={(e) => {
            e.stopPropagation();
            onDeleteInquiry(row._id || id);
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#ffe4e6'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#fff1f2'; }}
          title="Delete this inquiry"
        >
          🗑️ Delete
        </button>
      </div>
    )
  }
];

/**
 * Creates Internship table column definitions
 */
export const getInternshipColumns = ({ onDownloadResume, onDeleteInternship }) => [
  { key: 'name', label: 'Full Name' },
  { key: 'phone', label: 'Phone / Contact' },
  { key: 'city', label: 'City' },
  {
    key: 'applyFor',
    label: 'Applied Position',
    render: (val) => <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '3px 8px', borderRadius: '4px', fontWeight: 600, fontSize: '0.82rem' }}>{val}</span>
  },
  {
    key: 'createdAt',
    label: 'Applied Date',
    render: (val) => val ? new Date(val).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'
  },
  {
    key: 'resume',
    label: 'Resume Document',
    render: (val, row) => val && val.fileData ? (
      <button
        onClick={() => onDownloadResume(val, row.name)}
        style={{
          padding: '5px 12px',
          borderRadius: '6px',
          border: '1.5px solid #166534',
          background: '#f0fdf4',
          color: '#166534',
          fontWeight: 700,
          fontSize: '0.82rem',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px'
        }}
      >
        📥 Download Resume
      </button>
    ) : (
      <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '0.85rem' }}>No Resume</span>
    )
  },
  {
    key: '_id',
    label: 'Actions',
    render: (id, row) => (
      <button
        style={{ padding: '4px 10px', borderRadius: '6px', border: 'none', background: '#fee2e2', color: '#991b1b', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem' }}
        onClick={() => onDeleteInternship(row._id || id)}
      >
        Delete
      </button>
    )
  }
];
